import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw } from 'lucide-react';
import { apiConfigManager } from '../../config/apiConfig';

interface SmsVerificationInputProps {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (code?: string) => void;
  loading?: boolean;
  onResend?: () => void;
  resendCooldown?: number; // 重发冷却时间（秒）
}

const SmsVerificationInput: React.FC<SmsVerificationInputProps> = ({
  value,
  onChange,
  onComplete,
  loading = false,
  onResend,
  resendCooldown = 60
}) => {
  const [countdown, setCountdown] = useState(resendCooldown);
  const [canResend, setCanResend] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // const [autoFilled, setAutoFilled] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // 开发环境自动获取验证码 - 已禁用
  // const fetchLatestCode = async () => {
  //   if (!phoneNumber || process.env.NODE_ENV === 'production') {
  //     return null;
  //   }

  //   try {
  //     // 🔧 修复：使用apiConfigManager构建正确的API URL（即使在注释中也保持正确）
  //     const devCodeUrl = apiConfigManager.buildApiUrl(`/auth/dev/latest-code/${phoneNumber}`);
  //     const response = await fetch(devCodeUrl);
      
  //     if (!response.ok) {
  //       // API返回错误状态，静默处理
  //       return null;
  //     }
      
  //     const data = await response.json();
      
  //     if (data.success && data.code) {
  //       return data.code;
  //     }
  //   } catch (error) {
  //     // 静默处理网络错误，避免控制台噪音
  //     console.debug('🔧 [开发模式] 验证码暂未生成，稍后重试');
  //   }
  //   return null;
  // };

  // 倒计时逻辑
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  // 开发环境自动填充逻辑 - 已禁用
  // useEffect(() => {
  //   const autoFillCode = async () => {
  //     if (!phoneNumber || autoFilled || import.meta.env.MODE === 'production' || value.length === 6) {
  //       return;
  //     }

  //     // 延迟2秒后自动获取验证码
  //     const timer = setTimeout(async () => {
  //       const code = await fetchLatestCode();
  //       if (code) {
  //         console.log('🔧 [开发模式] 自动填充验证码:', code);
  //         onChange(code);
  //         setAutoFilled(true);
  //       }
  //     }, 2000);

  //     return () => clearTimeout(timer);
  //   };

  //   autoFillCode();
  // }, [phoneNumber, autoFilled, value.length]);

  // 当phoneNumber变化时重置自动填充状态 - 已禁用
  // useEffect(() => {
  //   setAutoFilled(false);
  // }, [phoneNumber]);

  // 处理输入
  const handleInputChange = (index: number, inputValue: string) => {
    // 只允许数字
    const numericValue = inputValue.replace(/\D/g, '');
    
    if (numericValue.length <= 1) {
      const newValue = value.split('');
      newValue[index] = numericValue;
      const updatedValue = newValue.join('').slice(0, 6);
      
      console.log('📝 更新验证码状态:', { index, inputValue, numericValue, updatedValue });
      onChange(updatedValue);
      
      // 自动跳转到下一个输入框
      if (numericValue && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
      
      // 自动完成（防重复提交）
      if (updatedValue.length === 6 && onComplete && !isSubmitting) {
        console.log('🔄 自动完成验证码输入:', updatedValue);
        setIsSubmitting(true);
        // 直接传递验证码，避免状态延迟问题
        setTimeout(() => {
          console.log('🚀 即将调用onComplete，传递验证码:', updatedValue);
          onComplete(updatedValue);
          // 延迟重置提交状态
          setTimeout(() => setIsSubmitting(false), 2000);
        }, 200);
      }
    }
  };

  // 处理键盘事件
  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // 处理粘贴
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onChange(pastedText);
    
    if (pastedText.length === 6 && onComplete && !isSubmitting) {
      setIsSubmitting(true);
      setTimeout(() => {
        onComplete(pastedText);
        setTimeout(() => setIsSubmitting(false), 2000);
      }, 100);
    }
  };

  // 重新发送验证码（防重复操作）
  const handleResend = () => {
    if (canResend && onResend && !loading) {
      setCanResend(false);
      setCountdown(resendCooldown);
      onResend();
    }
  };

  return (
    <div className="space-y-4">
      {/* 验证码输入框 */}
      <div className="flex justify-center space-x-3">
        {Array.from({ length: 6 }, (_, index) => (
          <input
            key={index}
            ref={(el) => inputRefs.current[index] = el}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={value[index] || ''}
            onChange={(e) => handleInputChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            className={`w-12 h-12 text-center text-xl font-bold border-2 rounded-lg 
              focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200
              ${value[index] ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}
              ${loading || isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={loading || isSubmitting}
          />
        ))}
      </div>

      {/* 验证按钮 */}
      <button
        type="button"
        onClick={() => {
          if (onComplete && !isSubmitting && value.length === 6) {
            setIsSubmitting(true);
            onComplete(value);
            setTimeout(() => setIsSubmitting(false), 2000);
          }
        }}
        disabled={loading || value.length !== 6 || isSubmitting}
        className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-medium"
      >
        {loading ? '验证中...' : isSubmitting ? '提交中...' : '验证并继续'}
      </button>

      {/* 重新发送 */}
      <div className="text-center">
        {canResend ? (
          <button
            type="button"
            onClick={handleResend}
            disabled={loading}
            className="flex items-center justify-center space-x-1 text-blue-600 hover:text-blue-700 transition-colors duration-200 mx-auto"
          >
            <RefreshCw className="w-4 h-4" />
            <span>重新发送验证码</span>
          </button>
        ) : (
          <p className="text-gray-500 text-sm">
            {countdown}秒后可重新发送
          </p>
        )}
      </div>

      {/* 提示信息 */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
        <p className="text-gray-700 text-sm">
          💡 <strong>提示:</strong> 请检查短信收件箱，验证码有效期为5分钟
        </p>
      </div>

    </div>
  );
};

export default SmsVerificationInput;